import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import { LOCALE_ID, NgModule } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { DynamicDialogModule } from 'primeng/dynamicdialog';
import { DividerModule } from 'primeng/divider';
import { FieldsetModule } from 'primeng/fieldset';
import { FloatLabelModule } from 'primeng/floatlabel';
import { ImageModule } from 'primeng/image';
import { InputOtpModule } from 'primeng/inputotp';
import { MenuModule } from 'primeng/menu';
import { MessageModule } from 'primeng/message';
import { StepperModule } from 'primeng/stepper';
import { TooltipModule } from 'primeng/tooltip';
import { AvatarModule } from 'primeng/avatar';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HeaderModule } from './components/global/header/header.module';
import { SidebarModule } from './components/global/sidebar/sidebar.module';
import { HomeComponent } from './pages/home/home.component';
import { DashboardCardModule } from './pages/home/components/dashboard-card/dashboard-card.module';
import { ServiceManagementCardModule } from './pages/home/components/service-management-card/service-management-card.module';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputTextModule } from 'primeng/inputtext';
import { LoginComponent } from './pages/auth/login/login.component';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { ConfirmSignInModule } from './pages/auth/components/confirm-sign-in/confirm-sign-in.module';
import { ForgetPasswordModule } from './pages/auth/components/forget-password/forget-password.module';
import { PasswordModule } from 'primeng/password';
import { RippleModule } from 'primeng/ripple';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { AuthInterceptor } from './core/auth/interceptors/auth.interceptor';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeng/themes/aura';
import { SessionExpiryDialogComponent } from './components/global/session-expiry-dialog/session-expiry-dialog.component';
import { DelegatedSessionDialogComponent } from './components/global/session-expiry-dialog/delegated-session-dialog.component';
import { SecurityProfileComponent } from './pages/modules/profile/security-profile.component';
import { PasswordFactorComponent } from './pages/modules/profile/components/password-factor/password-factor.component';
import { PasskeysFactorComponent } from './pages/modules/profile/components/passkeys-factor/passkeys-factor.component';
import { TwoFactorSectionComponent } from './pages/modules/profile/components/two-factor-section/two-factor-section.component';
import { RemovableTableSortDirective } from './shared/ui/removable-table-sort/removable-table-sort.directive';

registerLocaleData(localeEs);

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    HomeComponent,
    SessionExpiryDialogComponent,
    DelegatedSessionDialogComponent,
    SecurityProfileComponent,
    PasswordFactorComponent,
    PasskeysFactorComponent,
    TwoFactorSectionComponent,
  ],
  imports: [
    AppRoutingModule,
    AvatarModule,
    BrowserAnimationsModule,
    BrowserModule,
    ButtonModule,
    CardModule,
    CheckboxModule,
    ConfirmSignInModule,
    DashboardCardModule,
    DialogModule,
    DividerModule,
    DynamicDialogModule,
    FieldsetModule,
    FloatLabelModule,
    ForgetPasswordModule,
    FormsModule,
    ImageModule,
    InputOtpModule,
    MenuModule,
    MessageModule,
    HeaderModule,
    SidebarModule,
    HttpClientModule,
    InputGroupAddonModule,
    InputGroupModule,
    InputTextModule,
    PasswordModule,
    ReactiveFormsModule,
    RippleModule,
    ServiceManagementCardModule,
    SkeletonModule,
    StepperModule,
    TableModule,
    RemovableTableSortDirective,
    TabsModule,
    TagModule,
    ToastModule,
    TooltipModule,
  ],
  providers: [
    MessageService,
    DialogService,
    { provide: LOCALE_ID, useValue: 'es-ES' },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: '.dark',
        },
      },
      translation: {
        accept: 'Aceptar',
        reject: 'Rechazar',
        choose: 'Elegir',
        upload: 'Subir',
        cancel: 'Cancelar',
        dateFormat: 'dd/mm/yy',
        dayNames: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
        dayNamesShort: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
        // Indexado desde domingo; con firstDayOfWeek: 1 el p-datepicker los muestra como "Lu Ma Mi Ju Vi Sa Do".
        dayNamesMin: ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa'],
        monthNames: ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'],
        monthNamesShort: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
        today: 'Hoy',
        clear: 'Limpiar',
        apply: 'Aplicar',
        dateIs: 'La fecha es',
        dateIsNot: 'La fecha no es',
        dateBefore: 'La fecha es anterior a',
        dateAfter: 'La fecha es posterior a',
        weekHeader: 'Sem',
        firstDayOfWeek: 1,
        chooseDate: 'Elegir fecha',
        chooseMonth: 'Elegir mes',
        chooseYear: 'Elegir año',
        prevDecade: 'Década anterior',
        nextDecade: 'Década siguiente',
        prevYear: 'Año anterior',
        nextYear: 'Año siguiente',
        prevMonth: 'Mes anterior',
        nextMonth: 'Mes siguiente',
        prevHour: 'Hora anterior',
        nextHour: 'Hora siguiente',
        prevMinute: 'Minuto anterior',
        nextMinute: 'Minuto siguiente',
        prevSecond: 'Segundo anterior',
        nextSecond: 'Segundo siguiente',
        am: 'a. m.',
        pm: 'p. m.',
      },
    }),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
